import React, { useEffect, useCallback, memo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ActionBar from "../../../components/CRUDcomponents/Actionbar/ActionBar";
import DynamicTable from "../../../components/Table/IndentTable";
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import { fetchIndentData, fetchDropdownOptions, setSelectedRow, filterIndents, filterByDate } from '../../../store/indentSlice';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner'; // Create this component

const MemoizedDynamicTable = memo(DynamicTable);
const MemoizedActionBar = memo(ActionBar);

const Indent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { role, id: enteredBy } = useSelector((state) => state.auth);
  const {
    filteredIndents,
    dropdownData,
    selectedRow,
    isLoading,
    error
  } = useSelector((state) => state.indent);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const apiEndpoint = `${API_BASE_URL}/indent/indent`;

  useEffect(() => {
    dispatch(fetchIndentData({ role, enteredBy }));
    dispatch(fetchDropdownOptions());
  }, [dispatch, role, enteredBy]);

  const getRowColor = useCallback((row) => {
    return row.IsClosed ? "bg-green-100" : "";
  }, []);

  const getEditFields = useCallback(() => [
    {
      name: "ProjectID",
      label: "Project",
      type: "select",
      options: dropdownData.projects.map((project) => ({
        value: project.id,
        label: project.name,
      })),
    },
    {
      name: "TypeID",
      label: "Type",
      type: "select",
      options: dropdownData.types.map((type) => ({
        value: type.id,
        label: type.name,
      })),
    },
  ], [dropdownData.projects, dropdownData.types]);

  const handleSearch = useCallback((query) => {
    dispatch(filterIndents(query));
  }, [dispatch]);

  const handleDateFilter = useCallback((filterOptions) => {
    dispatch(filterByDate(filterOptions));
  }, [dispatch]);

  const handleRowClick = useCallback((row) => {
    navigate(`/viewTable`, { state: { row } });
  }, [navigate]);

  const handleRowSelect = useCallback((row) => {
    dispatch(setSelectedRow(row));
  }, [dispatch]);

  if (error) {
    return <div className="text-red-500 text-center p-4">Error: {error}</div>;
  }

  const columns = [
    { key: "IndentID", label: "Indent ID" },
    { key: "CreationDate", label: "Creation Date" },
    { key: "ProjectName", label: "Project" },
    { key: "TypeName", label: "Indent Type" },
    { key: "TaskSchedule", label: "Task Scheduled" },
    { key: "Status", label: "Status" },
    { key: "ItemCount", label: "Item Count" },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex-grow p-4">
        <div className="mt-6 mb-2 text-center">
          <h3 className="text-xl font-semibold">View Table</h3>
        </div>

        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            {dropdownData.users.length > 0 && dropdownData.projects.length > 0 && (
              <MemoizedActionBar
                title="Indent"
                fields={getEditFields()}
                apiEndpoint={apiEndpoint}
                data={filteredIndents}
                selectedRow={selectedRow}
                onRowSelect={handleRowSelect}
                onRefresh={() => dispatch(fetchIndentData({ role, enteredBy }))}
                onSearch={handleSearch}
                onFilter={handleDateFilter}
                rowKey="IndentID"
                userRole="ffff"
                addLink="/addIndent"
                editRoute="/viewTable"
                editButtonLabel="View"
              />
            )}

            <div className="mt-4 overflow-x-auto">
              <MemoizedDynamicTable
                columns={columns}
                data={filteredIndents}
                selectedRow={selectedRow}
                onRowSelect={handleRowSelect}
                onDoubleClick={handleRowClick}
                rowKey="IndentID"
                userIdKey="UserID"
                getRowClassName={getRowColor}
                noDataMessage="No indents found."
                userRole={role}
              />
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default memo(Indent);
